import { useEffect, useState } from 'react'
import { GameTitle } from '../../../components/GameTitle'
import { PageHeader } from '../../../components/PageHeader'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useAuth } from '../../../lib/auth'
import { useGatedAction } from '../../../lib/useSubscription'
import { readError } from '../../../lib/errors'
import {
  addThreeStepAi, createThreeStepRoom, fetchThreeStepAiPersonas,
  joinThreeStepRoomByCode, startThreeStepGame,
  type ThreeStepAiPersonaRow,
} from '../api/rooms'
import { useT } from '../../../i18n'

interface Props {
  onBack: () => void
  onOpenWeekly: () => void
  /** Routes into the room — caller subscribes to realtime + drives AIs. */
  onEnteredRoom: (roomId: string) => void
  /** Routes into the matchmaking-wait screen. */
  onOnlineMatch: () => void
  /** Phone Pass Play — local-only, single-device hot-seat. */
  onPassPlay: () => void
  onOpenPaywall: () => void
}

const MIN_AI = 1
// 6-seat table; subtract the host = up to 5 AI in Quick Play.
const MAX_AI = 5
// Sensible starting selection — first 3 personas the DB returns
// (alphabetical), user can toggle from there.
const DEFAULT_AI = 3

/**
 * Three Step Solution lobby — IET-shaped.
 *
 *   Quick Play vs AI  (primary CTA): host, fill seats with AIs of
 *                                    the user's choice, auto-start.
 *   Party Play        : host a room, share the 6-char code, friends
 *                       on their own phones tap "Join with code" to
 *                       enter. No phone-passing — everyone has their
 *                       own device.
 *   Join with code    : enter a code from a host.
 *   Online match      : matchmade — slot into the next 4-player game.
 *
 * All AI generation happens client-side via the persona templates in
 * `ai/personas.ts` — whoever is connected to the room runs the
 * autoplay loop on behalf of any AI seat that hasn't acted yet.
 */
export function ThreeStepLobbyScreen({
  onBack, onOpenWeekly, onEnteredRoom, onOnlineMatch, onPassPlay, onOpenPaywall,
}: Props) {
  const t = useT()
  const { profile, configured, updateProfile } = useAuth()
  const gate = useGatedAction(onOpenPaywall)
  const [mode, setMode] = useState<'choose' | 'create' | 'join'>('choose')
  const [isPrivate, setIsPrivate] = useState(false)
  const [roomCode, setRoomCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Display-name draft (matches IET + Colorful lobbies). The host's
  // and guests' display_name is what appears next to their seat in
  // the room and on the TV — we capture it here so a guest joining
  // with a code can rename themselves before entering the lobby
  // (otherwise they'd land in the room as whatever their profile said,
  // which is the literal "Player" fallback for users whose Apple
  // sign-in didn't share a name).
  // "Player" is the trigger fallback when Apple Sign-In doesn't share a
  // name — pre-filling it would let users tap right past the field
  // and land in the room as "Player" anyway. Treat it as empty so the
  // input visibly needs filling out.
  const initialName = profile?.display_name && profile.display_name !== 'Player'
    ? profile.display_name : ''
  const [nameDraft, setNameDraft] = useState<string>(initialName)
  useEffect(() => {
    if (profile?.display_name && profile.display_name !== 'Player') {
      setNameDraft(profile.display_name)
    }
  }, [profile?.display_name])

  // AI roster state — pool is the full active roster from the DB; the
  // user toggles which personas to include via `selectedIds`. We keep
  // selection as a Set keyed by id (not a `lineup` array) so order is
  // pool-stable and users see the same persona in the same spot every
  // visit. Cap at MAX_AI selections; UI prevents adding past that.
  const [pool, setPool] = useState<ThreeStepAiPersonaRow[] | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    fetchThreeStepAiPersonas()
      .then(rows => {
        if (cancelled) return
        setPool(rows)
        // Default selection: first DEFAULT_AI personas from the (alpha-
        // sorted) roster. Users can deselect/add from there.
        setSelectedIds(prev => {
          if (prev.size > 0) return prev   // already user-customised
          return new Set(rows.slice(0, DEFAULT_AI).map(r => r.id))
        })
      })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  // Derived lineup: personas the user has actually selected, in pool order.
  const lineup = (pool ?? []).filter(p => selectedIds.has(p.id))

  function togglePersona(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        // Don't let the user empty the lineup — they need ≥1 to play.
        if (next.size <= MIN_AI) return prev
        next.delete(id)
      } else {
        if (next.size >= MAX_AI) return prev   // capped
        next.add(id)
      }
      return next
    })
  }

  function canPlay(): boolean {
    if (!configured) { setError(t('Supabase is not configured yet.')); return false }
    if (!profile)    { setError(t('Sign in to play.')); return false }
    return true
  }

  async function persistNameIfChanged(): Promise<void> {
    const trimmed = nameDraft.trim()
    if (!trimmed || trimmed === profile?.display_name) return
    try { await updateProfile({ display_name: trimmed }) }
    catch (e) { console.warn('[3step-lobby] name update failed, continuing anyway', e) }
  }

  /**
   * Quick Play: spin up a private room, fill it with the chosen AI
   * lineup, fire start_three_step_game so the room transitions
   * lobby → phase1 immediately, then route into the room screen.
   */
  async function startQuickPlay() {
    if (!canPlay()) return
    if (lineup.length < MIN_AI) {
      setError(t('Pick at least one AI opponent.'))
      return
    }
    setBusy(true); setError(null)
    try {
      await persistNameIfChanged()
      const room = await createThreeStepRoom(true)
      for (const persona of lineup) {
        await addThreeStepAi(room.id, persona.id)
      }
      await startThreeStepGame(room.id)
      onEnteredRoom(room.id)
    } catch (err) {
      setError(readError(err))
    } finally {
      setBusy(false)
    }
  }

  async function doCreate() {
    if (!canPlay()) return
    setBusy(true); setError(null)
    try {
      await persistNameIfChanged()
      const room = await createThreeStepRoom(isPrivate)
      onEnteredRoom(room.id)
    } catch (err) {
      setError(readError(err))
    } finally {
      setBusy(false)
    }
  }

  async function doJoin() {
    if (!canPlay() || roomCode.length !== 6) return
    if (!nameDraft.trim()) {
      setError(t('Type a name first. It shows up next to your seat.'))
      return
    }
    setBusy(true); setError(null)
    try {
      await persistNameIfChanged()
      const room = await joinThreeStepRoomByCode(roomCode)
      onEnteredRoom(room.id)
    } catch (err) {
      setError(readError(err))
    } finally {
      setBusy(false)
    }
  }

  // ─── Choose mode ─────────────────────────────────────────────────────
  if (mode === 'choose') {
    const ready = pool !== null && lineup.length >= MIN_AI && !busy
    return (
      <div className="screen">
        <BackgroundTitle text="Three Step Solution" color="#0EAE93" />
        <PageHeader
          title={t('Three Step Solution')}
          subtitle={t('Two actions, three steps')}
          onBack={onBack}
        />

        <div className="flex-1 flex flex-col px-5 py-5 gap-5 overflow-y-auto pb-36 relative z-10 glass-panes">
          <div className="flex items-center gap-3">
            <img src="/games/three-step.webp" alt="Three Step Solution"
                 className="w-16 h-16 rounded-2xl object-cover shadow-card shrink-0" />
            <div className="min-w-0">
              <GameTitle game="three-step" size="md" />
              <p className="text-xs text-brand-muted mt-1">
                {t('Combine two actions, write three steps. Most votes wins.')}
              </p>
            </div>
          </div>

          {/* Your name — shows on the TV + everyone else's seat list. */}
          <div>
            <label htmlFor="3step-lobby-name"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Your name')}
            </label>
            <input
              id="3step-lobby-name"
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value.slice(0, 24))}
              onBlur={() => { void persistNameIfChanged() }}
              maxLength={24}
              placeholder={t('Jodan')}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                         text-brand-text placeholder:text-brand-muted/60
                         font-display text-lg font-bold
                         focus:outline-hidden focus:border-game-three-step-dark"
            />
          </div>

          {/* How it plays */}
          <div className="bg-game-three-step-light rounded-2xl shadow-card p-4
                          border-2 border-game-three-step-dark">
            <p className="text-xs font-semibold text-brand-text uppercase tracking-wide mb-2">
              {t('How it plays')}
            </p>
            <ul className="text-sm text-brand-text space-y-1.5">
              <li>{t('• Each player writes 3 actions: things you do, or things that happen to you.')}</li>
              <li>{t('• Two random actions, one from each of two different players, get combined into a prompt.')}</li>
              <li>{t('• Each player writes 3 steps that answer the prompt.')}</li>
              <li>• Read the answers, vote on your favorite (not yours). Three rounds. Most votes wins.</li>
            </ul>
          </div>

          {/* Play with friends — promoted above the AI picker so the
              first thing the user sees is the social option. The "Or"
              prefix moves to the AI section below per Jordan's
              consistency request across all three games. */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">
              {t('Play with friends')}
            </p>

            <button
              onClick={() => gate(() => setMode('create'))}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">🎉</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Party play with friends')}</p>
                <p className="text-xs text-brand-muted">{t('Host a room, public or private. Share a 6-character code so friends can join from their own phones.')}</p>
              </div>
            </button>

            <button
              onClick={() => setMode('join')}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">🔑</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Join a room')}</p>
                <p className="text-xs text-brand-muted">{t('Enter a 6-character code from a friend.')}</p>
              </div>
            </button>

            <button
              onClick={() => { if (!busy) gate(onOnlineMatch) }}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">🌐</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Online match')}</p>
                <p className="text-xs text-brand-muted">{t('Get matched with 3 other players. Take turns on your own time. The game waits for you.')}</p>
              </div>
            </button>

            <button
              onClick={() => { if (!busy) gate(onPassPlay) }}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">📱</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Phone Pass Play')}</p>
                <p className="text-xs text-brand-muted">{t('Local only. Pass the phone between people in the room. No internet needed.')}</p>
              </div>
            </button>

            <button
              onClick={onOpenWeekly}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">📆</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Play the weekly')}</p>
                <p className="text-xs text-brand-muted">
                  {t('One prompt for the whole community.')}
                </p>
              </div>
            </button>
          </div>

          {/* Or play with AI opponents — multi-select picker. Lives
              below the friends section now per the reordered hierarchy. */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">
                {t('Or play with AI opponents')}
              </p>
              <p className="text-xs text-brand-muted">
                {lineup.length}/{MAX_AI} picked
              </p>
            </div>
            {error && (
              <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-2">
                <p className="text-sm text-brand-danger">{error}</p>
              </div>
            )}
            {pool === null ? (
              <div className="space-y-2">
                <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
                <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
                <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
              </div>
            ) : (
              <ul className="space-y-2">
                {pool.map(p => {
                  const selected = selectedIds.has(p.id)
                  const atCap = !selected && lineup.length >= MAX_AI
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => togglePersona(p.id)}
                        disabled={atCap}
                        aria-pressed={selected}
                        className={
                          'w-full p-3 flex items-center gap-3 rounded-2xl text-left ' +
                          'transition-colors active:scale-[0.99] ' +
                          (selected
                            ? 'bg-game-three-step-light border-2 border-game-three-step-dark shadow-card'
                            : 'bg-white border border-brand-border ') +
                          (atCap ? ' opacity-40' : '')
                        }
                      >
                        <span className="text-2xl shrink-0">{p.avatar_emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-brand-text text-sm">{p.name}</p>
                          <p className="text-xs text-brand-muted line-clamp-2">{t(p.persona)}</p>
                        </div>
                        <span
                          className={
                            'shrink-0 w-6 h-6 rounded-full flex items-center justify-center ' +
                            'text-sm font-bold border-2 transition-colors ' +
                            (selected
                              ? 'bg-game-three-step-dark border-game-three-step-dark text-white'
                              : 'bg-white border-brand-border text-brand-muted')
                          }
                          aria-hidden="true"
                        >
                          {selected ? '✓' : '+'}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="floating-actions">
          <button
            onClick={() => gate(startQuickPlay)}
            disabled={!ready}
            className="btn-three-step
                       disabled:opacity-40"
          >
            {busy ? t('Setting up table…') : t('Quick Play vs AI')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Join mode ───────────────────────────────────────────────────────
  if (mode === 'join') {
    return (
      <div className="screen">
        <PageHeader title={t('Join Room')} onBack={() => { setMode('choose'); setError(null) }} />
        <div className="flex-1 px-5 py-8 space-y-6">
          <div className="text-center">
            <img src="/games/three-step.webp" alt="Three Step Solution"
                 className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-card" />
            <h2 className="font-display text-xl font-bold text-brand-text">{t('Enter room code')}</h2>
            <p className="text-brand-muted text-sm mt-1">{t('Ask your host for the 6-character code')}</p>
          </div>
          <div>
            <label htmlFor="3step-join-name"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Your name')}
            </label>
            <input
              id="3step-join-name"
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value.slice(0, 24))}
              onBlur={() => { void persistNameIfChanged() }}
              maxLength={24}
              placeholder={t('Jodan')}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                         text-brand-text placeholder:text-brand-muted/60
                         font-display text-lg font-bold
                         focus:outline-hidden focus:border-game-three-step-dark"
            />
          </div>
          <div>
            <label htmlFor="3step-join-code"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Room code')}
            </label>
            <input
              id="3step-join-code"
              type="text"
              value={roomCode}
              onChange={e => setRoomCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 6))}
              placeholder="ABC123"
              inputMode="text"
              autoCapitalize="characters"
              className="w-full px-4 py-4 rounded-2xl border border-brand-border bg-white
                         text-brand-text text-center text-2xl font-display font-bold
                         tracking-[0.2em] placeholder:text-brand-border
                         focus:outline-hidden focus:border-game-three-step-dark"
            />
          </div>
          {error && <p className="text-sm text-brand-danger text-center">{error}</p>}
          <button
            onClick={doJoin}
            disabled={busy || roomCode.length !== 6 || !nameDraft.trim()}
            className="btn-three-step
                       disabled:opacity-40"
          >
            {busy ? 'Joining…' : t('Join Room')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Create mode ─────────────────────────────────────────────────────
  return (
    <div className="screen">
      <PageHeader title={t('Create Room')} onBack={() => { setMode('choose'); setError(null) }} />
      <div className="flex-1 px-5 py-8 space-y-6">
        <div className="text-center">
          <img src="/games/three-step.webp" alt="Three Step Solution"
               className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-card" />
          <h2 className="font-display text-xl font-bold text-brand-text">{t('New room')}</h2>
          <p className="text-brand-muted text-sm mt-1">{t("You'll get a 6-character code to share.")}</p>
        </div>

        <label className="card p-4 flex items-center justify-between cursor-pointer">
          <div>
            <p className="font-semibold text-brand-text">{t('Private room')}</p>
            <p className="text-sm text-brand-muted mt-0.5">
              {t('Only people with the code can join.')}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isPrivate}
            onClick={() => setIsPrivate(v => !v)}
            className={`relative w-12 h-7 rounded-full transition-colors ${isPrivate ? 'bg-game-three-step-dark' : 'bg-brand-border'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-xs
                              transition-transform ${isPrivate ? 'translate-x-5' : ''}`} />
          </button>
        </label>

        {error && <p className="text-sm text-brand-danger text-center">{error}</p>}

        <button
          onClick={doCreate}
          disabled={busy}
          className="btn-three-step
                     disabled:opacity-40"
        >
          {busy ? 'Creating…' : t('Create Room')}
        </button>
      </div>
    </div>
  )
}
